import { useState, useRef } from 'react';
import { useStripe, useElements, IbanElement } from '@stripe/react-stripe-js';
import { Loader2, Lock, AlertTriangle } from 'lucide-react';
import { getSepaErrorMessage } from '../utils/stripeErrors';

interface SepaSetupFormProps {
  clientSecret: string;
  onSuccess: () => void;
  companyName?: string;
  monthlyAmount?: number;
  duration?: number;
}

const IBAN_ELEMENT_OPTIONS = {
  supportedCountries: ['SEPA'],
  style: {
    base: {
      fontSize: '16px',
      color: '#333333',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': { color: '#aab7c4' },
      iconColor: '#8074af',
    },
    invalid: {
      color: '#df1b41',
      iconColor: '#df1b41',
    },
  },
};

/**
 * Formulaire SEPA pour SetupIntent (paiement mensuel).
 * Utilise confirmSepaDebitSetup pour enregistrer le mandat SEPA.
 */
export function SepaSetupForm({ clientSecret, onSuccess, companyName, monthlyAmount, duration }: SepaSetupFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const clickedOnceRef = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || clickedOnceRef.current || !stripe || !elements) return;

    clickedOnceRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(getSepaErrorMessage(submitError));
        clickedOnceRef.current = false;
        setLoading(false);
        return;
      }

      const ibanElement = elements.getElement(IbanElement);
      if (!ibanElement) {
        setError('Veuillez renseigner un IBAN valide.');
        clickedOnceRef.current = false;
        setLoading(false);
        return;
      }

      // @ts-ignore - confirmSepaDebitSetup exists on stripe
      const { error: confirmError } = await stripe.confirmSepaDebitSetup(clientSecret, {
        payment_method: {
          sepa_debit: ibanElement,
          billing_details: { name, email },
        },
      });

      if (confirmError) {
        // Vérifier le statut réel du SetupIntent avant d'afficher l'erreur
        const { setupIntent } = await stripe.retrieveSetupIntent(clientSecret);
        if (setupIntent?.status === 'succeeded') {
          onSuccess();
          return;
        }
        setError(getSepaErrorMessage(confirmError));
        clickedOnceRef.current = false;
        setLoading(false);
        return;
      }

      onSuccess();
    } catch (err) {
      setError(getSepaErrorMessage(err));
      clickedOnceRef.current = false;
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="tw-space-y-4">
      {/* IBAN */}
      <div>
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
          IBAN
        </label>
        <div className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-3">
          <IbanElement options={IBAN_ELEMENT_OPTIONS} />
        </div>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
          Format : FR76 XXXX XXXX XXXX XXXX XXXX XXX
        </p>
      </div>

      {/* Nom & Email */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-3">
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Prénom et Nom
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jean Dupont"
            required
            disabled={loading}
            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm disabled:tw-opacity-50"
          />
        </div>
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@exemple.fr"
            required
            disabled={loading}
            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm disabled:tw-opacity-50"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3 tw-flex tw-items-start tw-gap-2">
          <AlertTriangle className="tw-w-4 tw-h-4 tw-text-red-500 tw-flex-shrink-0 tw-mt-0.5" />
          <p className="tw-text-sm tw-text-red-700 tw-mb-0">{error}</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={!stripe || loading}
        className="tw-w-full tw-bg-affinicar-primary tw-text-white tw-rounded-lg tw-px-4 tw-py-3 tw-font-medium tw-text-sm hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-justify-center tw-gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
            Traitement en cours...
          </>
        ) : (
          'Signer le mandat et procéder au paiement'
        )}
      </button>

      {/* SEPA mandate text */}
      <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-space-y-2">
        {companyName && monthlyAmount && duration ? (
          <>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
              En cliquant sur &laquo;&nbsp;Signer le mandat et procéder au paiement&nbsp;&raquo;,
              vous signez électroniquement un mandat de prélèvement SEPA vous engageant à régler
              votre contrat en {duration} mensualités de {monthlyAmount.toFixed(2)}&euro;.
            </p>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
              En acceptant ce mandat, vous autorisez {companyName} à envoyer des instructions
              à votre banque pour débiter votre compte, et votre banque à débiter votre compte
              conformément aux instructions de {companyName}.
            </p>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
              Vous acceptez aussi de recevoir des notifications de futurs débits jusqu'à 2 jours
              avant qu'ils adviennent.
            </p>
          </>
        ) : (
          <>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
              En cliquant sur &laquo;&nbsp;Signer le mandat et procéder au paiement&nbsp;&raquo;, vous signez
              un mandat de prélèvement SEPA autorisant le débit de votre compte
              conformément aux conditions de votre contrat.
            </p>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
              Vous acceptez de recevoir des notifications de futurs débits jusqu'à 2 jours
              avant qu'ils adviennent.
            </p>
          </>
        )}
      </div>

      {/* Security badge */}
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-gray-400">
        <Lock className="tw-w-3 tw-h-3" />
        <span className="tw-text-xs">Paiement sécurisé via Stripe</span>
      </div>
    </form>
  );
}
